import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, MessageCircle, Search } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useConversations } from "@/hooks/queries";
import { useDebounce } from "@/hooks/useDebounce";
import { formatRelative } from "@/lib/format";

export function SearchDialog({ open, onOpenChange }) {
  const [q, setQ] = useState("");
  const debounced = useDebounce(q.trim(), 250);
  const { data, isFetching } = useConversations(debounced, 20);
  const navigate = useNavigate();
  const items = data?.items || [];

  const go = (id) => {
    onOpenChange(false);
    setQ("");
    navigate(`/chat/${id}`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="top-[18%] max-w-xl translate-y-0 gap-0 overflow-hidden border-white/10 bg-ink-850 p-0 [&>button]:hidden" data-testid="search-dialog">
        <DialogTitle className="sr-only">Search conversations</DialogTitle>
        <div className="flex items-center gap-3 border-b border-white/[0.07] px-4">
          <Search className="h-4 w-4 text-zinc-500" />
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && items[0] && go(items[0].id)}
            placeholder="Search your conversations…"
            maxLength={200}
            data-testid="search-dialog-input"
            className="h-14 flex-1 bg-transparent text-[15px] text-white placeholder:text-zinc-500 focus:outline-none"
          />
          {isFetching && <Loader2 className="h-4 w-4 animate-spin text-zinc-500" />}
        </div>
        <div className="max-h-[50vh] overflow-y-auto p-2" data-testid="search-dialog-results">
          {items.length === 0 && !isFetching && (
            <div className="px-3 py-10 text-center text-sm text-zinc-500" data-testid="search-dialog-empty">
              {debounced ? "No conversations match your search." : "No conversations yet."}
            </div>
          )}
          {items.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => go(c.id)}
              data-testid={`search-result-${c.id}`}
              className="flex w-full items-start gap-3 rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-white/[0.05]"
            >
              <MessageCircle className="mt-0.5 h-4 w-4 shrink-0 text-zinc-500" strokeWidth={1.6} />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm text-zinc-100">{c.title}</span>
                {c.snippet && <span className="mt-0.5 line-clamp-1 block text-xs text-zinc-500">{c.snippet}</span>}
              </span>
              <span className="shrink-0 text-[11px] text-zinc-600">{formatRelative(c.last_message_at)}</span>
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
